import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Box } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { fetchCustomers } from '../api/authApi';
import { PageHeader } from '../components/PageHeader';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { TableToolbar } from '../components/TableToolbar';
import { SectionCard } from '../components/SectionCard';
import { DataTable } from '../components/DataTable';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { StatusBadge } from '../components/StatusBadge';
import { IconButton } from '../components/ui/IconButton';
import { TextInput } from '../components/ui/TextInput';

const CUSTOMER_STATUSES = ['active', 'suspended'];

export function CustomersPage() {
  const { t } = useTranslation(['customers', 'common', 'statuses']);
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    setLoading(true);
    setError(null);

    const params = {};
    if (search.trim()) {
      params.search = search.trim();
    }
    if (status) {
      params.status = status;
    }

    fetchCustomers(params)
      .then((response) => setCustomers(response.data || []))
      .catch(() => setError(t('customers:load_failed')))
      .finally(() => setLoading(false));
  }, [search, status, t]);

  const emptyValue = t('common:empty_value');
  const recordCount = customers.length;

  const columns = useMemo(
    () => [
      {
        id: 'id',
        label: t('common:id'),
        width: 72,
        render: (row) => row.id ?? emptyValue,
      },
      {
        id: 'name',
        label: t('common:name'),
        render: (row) => row.name || emptyValue,
      },
      {
        id: 'slug',
        label: t('common:slug'),
        render: (row) => row.slug || emptyValue,
      },
      {
        id: 'email',
        label: t('common:email'),
        render: (row) => row.billing_email || emptyValue,
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (row) => (
          row.status
            ? <StatusBadge status={row.status} category="customer" size="Sm" />
            : emptyValue
        ),
      },
      {
        id: 'tenants',
        label: t('common:tenants'),
        align: 'right',
        render: (row) => (
          row.tenants_count === 0 || row.tenants_count
            ? row.tenants_count
            : emptyValue
        ),
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        width: 72,
        render: (row) => (
          <IconButton
            size="Sm"
            tone="Neutral"
            aria-label={t('common:view_detail')}
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/customers/${row.id}`);
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        ),
      },
    ],
    [emptyValue, navigate, t],
  );

  return (
    <Box>
      <PageHeader
        type="List"
        title={t('customers:title')}
        subtitle={t('customers:list_subtitle')}
      />

      <FilterBar type="Simple">
        <TextInput
          density="Compact"
          hideLabel
          label={t('common:search')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <FilterSelect
          label={t('common:status')}
          hideLabel
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {CUSTOMER_STATUSES.map((value) => (
            <FilterSelectOption key={value} value={value}>
              {t(`statuses:customer.${value}`)}
            </FilterSelectOption>
          ))}
        </FilterSelect>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding>
        <TableToolbar
          mode="Default"
          title={t('customers:title')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('customers:toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={customers}
            emptyMessage={t('customers:empty_list')}
            onRowClick={(row) => navigate(`/customers/${row.id}`)}
          />
        )}
      </SectionCard>
    </Box>
  );
}
